import { ProjectFormData } from '../types/project-form';

/**
 * Scrolls to the first error field in the form
 */
export const scrollToFirstError = (): void => {
  const errorFields = document.querySelectorAll('[aria-invalid="true"]');

  if (errorFields.length > 0) {
    const firstErrorField = errorFields[0] as HTMLElement;
    
    firstErrorField.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });

    // Focus the field if it's focusable
    setTimeout(() => {
      if (firstErrorField.focus) {
        firstErrorField.focus();
      }
    }, 500);
  }
};

/**
 * Transforms fishbone analysis data from frontend nested structure to backend flat arrays
 */
export const transformFishboneDataForBackend = (formData: ProjectFormData) => {
  const result: any = {};

  if (formData.fishBoneAnalysis) {
    const categories = ['peopleCauses', 'processCauses', 'policyCauses', 'placeCauses', 'provisionCauses'] as const;
    
    categories.forEach(category => {
      const categoryData = formData.fishBoneAnalysis?.[category];
      if (categoryData) {
        if (categoryData.causeItems && Array.isArray(categoryData.causeItems)) {
          // New structured format with causeItems array
          result[category] = categoryData.causeItems.map((item: any) => ({
            text: item.cause || '',
            why1: item.whyAnalysis?.why1 || '',
            why2: item.whyAnalysis?.why2 || '',
            why3: item.whyAnalysis?.why3 || '',
            why4: item.whyAnalysis?.why4 || '',
            why5: item.whyAnalysis?.why5 || '',
            rootCause: item.whyAnalysis?.rootCause || '',
            rank: item.rank || 0
          }));
        } else if (categoryData.causes) {
          // Fall back to legacy comma-separated causes format
          const causes = categoryData.causes.split(',').map((cause: string) => cause.trim()).filter(Boolean);
          result[category] = causes.map((causeText: string) => ({
            text: causeText,
            why1: categoryData.whyAnalysis?.why1 || '',
            why2: categoryData.whyAnalysis?.why2 || '',
            why3: categoryData.whyAnalysis?.why3 || '',
            why4: categoryData.whyAnalysis?.why4 || '',
            why5: categoryData.whyAnalysis?.why5 || '',
            rootCause: categoryData.whyAnalysis?.rootCause || '',
            rank: 0
          }));
        }
      }
    });
  }

  return result;
};

/**
 * Prepares project data for submission by ensuring required fields and status
 */
export const prepareProjectData = (formData: ProjectFormData) => {
  const project = { ...formData };

  // CRITICAL: Ensure project is created with status 'ongoing' not 'draft'
  project.status = 'ongoing';

  // Ensure identifiedProblems is an array and filter out empty strings
  if (!Array.isArray(project.identifiedProblems)) {
    project.identifiedProblems = [];
  } else {
    project.identifiedProblems = project.identifiedProblems.filter(problem => 
      problem && typeof problem === 'string' && problem.trim() !== ''
    );
  }

  // Ensure potentialGaps is an array and filter out empty strings
  if (!Array.isArray(project.potentialGaps)) {
    project.potentialGaps = [];
  } else {
    project.potentialGaps = project.potentialGaps.filter(gap => 
      gap && typeof gap === 'string' && gap.trim() !== ''
    );
  }

  return project;
};

/**
 * Filters empty gaps from potential gaps array
 */
export const filterEmptyGaps = (gaps: string[]): string[] => {
  return gaps.filter(gap => gap && gap.trim() !== '');
};
